import React, { useState } from 'react';
import {
  Layout, Plus, Database, Download, Share2, RefreshCw, Menu, X, Search
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ShelfRow } from '@/components/ShelfManager/ShelfRow';
import { AddProductModal } from '@/components/ShelfManager/AddProductModal';
import { toast } from 'sonner';

const GONDOLAS = [1, 2, 3, 4, 5, 6];
const SHELVES = [7, 6, 5, 4, 3, 2, 1];

interface Product {
  id: string;
  name: string;
  category: string;
  image: string;
  sku: string;
  price: string;
  position?: string;
}

export default function Index() {
  const [products, setProducts] = useState<Product[]>([]);
  const [placedProducts, setPlacedProducts] = useState<Product[]>([]);
  const [currentGondola, setCurrentGondola] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const handleAddProduct = (prod: Product) => {
    setProducts([...products, prod]);
    toast.success(`${prod.name} added to library!`);
  };

  const handlePlaceProduct = (shelf: number, slot: number) => {
    if (!selectedProduct) return;
    const position = `${currentGondola}-${shelf}-${slot}`;
    const existing = placedProducts.find(p => p.position === position);

    if (existing) {
      setPlacedProducts(placedProducts.filter(p => p.position !== position));
      toast.info('Product slot cleared');
      return;
    }

    const newPlaced = {
      ...selectedProduct,
      position
    };
    setPlacedProducts([...placedProducts, newPlaced]);
    setSelectedProduct(null);
    toast.success(`Placed on Gondola ${currentGondola}, Shelf ${shelf}`);
  };

  const handleRemoveProduct = (shelf: number, slot: number) => {
    const position = `${currentGondola}-${shelf}-${slot}`;
    setPlacedProducts(placedProducts.filter(p => p.position !== position));
    toast.info('Product removed');
  };

  const handleEditProduct = (prod: Product) => {
    setEditingProduct(prod);
    setShowAddModal(true);
  };

  const handleExport = () => {
    const data = {
      gondolas: GONDOLAS,
      shelves: SHELVES,
      layout: placedProducts.map(p => ({
        productId: p.id,
        name: p.name,
        sku: p.sku,
        position: p.position,
        price: p.price
      }))
    };

    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `shelf-layout-${Date.now()}.json`;
    a.click();
    toast.success('Layout exported!');
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary text-card flex items-center justify-center">
              <Layout size={20} />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-foreground">Shelf Manager</h1>
              <p className="text-xs text-muted-foreground">Visual merchandising tool</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => setShowAddModal(true)}
              size="sm"
              className="gap-2"
            >
              <Plus size={16} />
              <span className="hidden sm:inline">Add Product</span>
            </Button>
            <Button
              onClick={handleExport}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <Download size={16} />
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>

        {/* Gondola Selector */}
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          <span className="text-xs font-bold text-muted-foreground whitespace-nowrap">Gondola:</span>
          <div className="flex gap-2">
            {GONDOLAS.map(g => (
              <button
                key={g}
                onClick={() => setCurrentGondola(g)}
                className={`px-3 py-1 rounded-lg font-bold text-sm transition-all ${
                  currentGondola === g
                    ? 'bg-primary text-card'
                    : 'bg-secondary text-foreground hover:bg-muted'
                }`}
              >
                {g}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Shelves Panel */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="max-w-6xl mx-auto">
            {selectedProduct && (
              <div className="mb-6 p-4 rounded-lg bg-primary/10 border border-primary/20 flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-xs text-muted-foreground mb-1">Selected Product</p>
                  <p className="font-bold text-foreground">{selectedProduct.name}</p>
                </div>
                <button
                  onClick={() => setSelectedProduct(null)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            <div>
              {SHELVES.map(shelf => (
                <ShelfRow
                  key={shelf}
                  shelf={shelf}
                  gondola={currentGondola}
                  products={placedProducts}
                  onDrop={handlePlaceProduct}
                  onRemove={handleRemoveProduct}
                  onEdit={handleEditProduct}
                  activeProduct={selectedProduct}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className={`fixed sm:relative inset-0 sm:inset-auto w-full sm:w-80 bg-card border-l border-border flex flex-col transition-all z-50 ${
          showLibrary ? 'translate-x-0' : 'translate-x-full sm:translate-x-0'
        }`}>
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="font-bold text-foreground">Product Library</h2>
            <button
              onClick={() => setShowLibrary(false)}
              className="sm:hidden text-muted-foreground hover:text-foreground"
            >
              <X size={18} />
            </button>
          </div>

          <div className="p-4 border-b border-border">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search products..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-2">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Database size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">{searchTerm ? 'No products found' : 'No products yet'}</p>
              </div>
            ) : (
              filteredProducts.map(prod => (
                <button
                  key={prod.id}
                  onClick={() => {
                    setSelectedProduct(prod);
                    setShowLibrary(false);
                  }}
                  className={`w-full p-3 rounded-lg text-left transition-all border ${
                    selectedProduct?.id === prod.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-primary/50 bg-secondary'
                  }`}
                >
                  <div className="flex gap-2">
                    <img
                      src={prod.image}
                      alt={prod.name}
                      className="w-12 h-12 rounded object-cover"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm text-foreground truncate">{prod.name}</p>
                      <p className="text-xs text-muted-foreground">{prod.sku}</p>
                      <p className="text-xs font-bold text-primary mt-1">{prod.price}</p>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Sidebar Toggle */}
        <button
          onClick={() => setShowLibrary(!showLibrary)}
          className="sm:hidden absolute bottom-6 right-6 z-40 p-3 rounded-full bg-primary text-card shadow-lg hover:shadow-xl transition-all"
        >
          <Menu size={20} />
        </button>
      </div>

      {/* Add Product Modal */}
      {showAddModal && (
        <AddProductModal
          onClose={() => {
            setShowAddModal(false);
            setEditingProduct(null);
          }}
          onAdd={handleAddProduct}
        />
      )}
    </div>
  );
}